import Store from '@ember-data/store';
import { assert } from '@ember/debug';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ENV from 'donor-elf-web/config/environment';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import DashboardModel from 'donor-elf-web/models/dashboard';
import FundModel from 'donor-elf-web/models/fund';
import GoalModel from 'donor-elf-web/models/goal';
import CachedDataset from 'donor-elf-web/services/cached-dataset';
import RSVP from 'rsvp';
import CurrentUser from './current-user';

export interface GetAllFundsFilter {
  has_ability_to_view_contact_name?: boolean;
}

export default class FundsService extends Service {
  @service declare cachedDataset: CachedDataset;
  @service declare currentUser: CurrentUser;
  @service declare store: Store;

  @tracked selectedFund?: FundModel;
  @tracked selectedFundToken: string = '';

  @tracked dashboard?: DashboardModel;
  // This will be populated from AllSettingsModel when initially entering a fund
  // and it will be repopulated every time the user goes to the dashboard page
  // The reason we use this (instead of using this.dashboard.goal) is because the
  // dashboard model only gets loaded when the user visits the dashboard page, but
  // there are other pages that need to know what type of goal the fund has, so
  // we need to populate this.goal immediately
  @tracked goal?: GoalModel;

  async populateSelectedFundToken() {
    assert(
      'this.selectedFund must be set before calling this method',
      this.selectedFund != null,
    );

    let record = await this.store.findRecord(
      'selected-fund-token',
      this.selectedFund.id,
    );
    let token = record.token;
    if (token == null) {
      token = '';
    }
    this.selectedFundToken = token;
  }

  viewFund(fundId: string, options: { urlPath?: string } = {}) {
    window.location.assign(this.viewFundUrl(fundId, options));
  }

  // used for manually setting the href on an anchor tag to open a new tab
  viewFundUrl(fundId: string, options: { urlPath?: string } = {}) {
    let newLocation = `${(ENV as any).rootURL}funds/${fundId}`;
    if (options.urlPath) {
      newLocation = `${newLocation}/${options.urlPath}`;
    }
    return newLocation;
  }

  // this caches the response
  _getAllFundsPromise?: Promise<FundModel[]>;
  _getAllFundsPromiseFulfilled = false;
  getAllFunds(args?: {
    filter?: GetAllFundsFilter;
    onRecordsFromServer?: (serverArgs: { records: FundModel[] }) => void;
  }): Promise<FundModel[]> {
    if (this._getAllFundsPromise) {
      return this._getAllFundsPromise;
    }

    const queryOptions: any = {};

    if (args?.filter != null) {
      queryOptions.filter = args.filter;
    }

    this._getAllFundsPromise = this.cachedDataset.forModel('fund').load();

    this._getAllFundsPromise.then(() => {
      this._getAllFundsPromiseFulfilled = true;
    });
    return this._getAllFundsPromise;
  }

  // this uses the cached response from `getAllFunds` above if it exists
  // otherwise it gets the data from the server immediately
  // This is not meant to be called with more than 50 or so fundIds
  getFunds(fundIds: string[]): Promise<FundModel[]> {
    if (this._getAllFundsPromiseFulfilled) {
      return this._getAllFundsPromise!.then((fundModels) => {
        return fundModels.filter((x) => fundIds.indexOf(x.id) >= 0);
      });
    } else {
      return queryWithPages<FundModel>({
        store: this.store,
        modelName: 'fund',
        queryOptions: {
          filter: {
            id: fundIds,
          },
        },
      });
    }
  }

  _allFundsInfoPromise?: Promise<AllFundsInfo>;
  getAllFundsInfo(): Promise<AllFundsInfo> {
    if (this._allFundsInfoPromise) {
      return this._allFundsInfoPromise;
    }

    let allFunds = new AllFundsInfo();
    return (this._allFundsInfoPromise = new Promise((resolve) => {
      this.getAllFunds({
        onRecordsFromServer: (args) => {
          allFunds._handleRecordsFromServer(args.records);
        },
      }).then((funds) => {
        allFunds._populateFunds(funds);
        resolve(allFunds);
      });
    }));
  }

  // todo: this shouldn't be needed anymore
  unload() {
    this.selectedFund = undefined;
    this.selectedFundToken = '';
    this.dashboard = undefined;
    this.goal = undefined;
  }

  // todo: remove all calls to this and delete this function
  refreshSelectedFundToken() {
    // no longer need this
    return RSVP.resolve();
    // let selectedFundPromise = null;
    // if (this.selectedFund) {
    //   selectedFundPromise = RSVP.resolve(this.selectedFund);
    // } else {
    //   selectedFundPromise = this.store
    //     .findRecord('fund', this.currentUser.selectedFundId).then(fund => {
    //     return this.selectedFund = fund;
    //   });
    // }

    // return selectedFundPromise.then(selectedFund => {
    //   return this.store.findRecord('selected-fund-token', selectedFund.id).then(record => {
    //     let token = record.token;
    //     if (token == null) {
    //       token = '';
    //     }
    //     this.setNewFundToken(token);
    //   });
    // });
  }

  // todo: remove all calls to this and delete this function
  confirmValidFundToken() {
    // no longer need this
    return RSVP.resolve();
    // if (isEmpty(this.selectedFundTokenExpiresAt)) {
    //   return this.refreshSelectedFundToken();
    // } else if (this.selectedFundTokenExpiresAt > moment()) {
    //   return RSVP.resolve();
    // } else {
    //   return this.refreshSelectedFundToken();
    // }
  }
}

export class AllFundsInfo {
  // intentionally not using @tracked because we never overwrite the array object
  funds: FundModel[] = [];
  fundsById: { [fundId: string]: FundModel } = {};
  _callbacks?: AllFundsRecordsFromServerCallback[] = [];

  onRecordsFromServer(callback: AllFundsRecordsFromServerCallback) {
    if (this._callbacks) {
      this._callbacks.push(callback);
    }
  }

  _populateFunds(funds: FundModel[]) {
    this.funds.length = 0;
    funds.forEach((fund) => {
      this.funds.push(fund);
      this.fundsById[fund.id] = fund;
    });
  }

  _handleRecordsFromServer(funds: FundModel[]) {
    this._populateFunds(funds);
    if (this._callbacks) {
      this._callbacks.forEach((callback) => {
        callback({ funds: this.funds, fundsById: this.fundsById });
      });
    }
    this._callbacks = undefined;
  }
}

export interface AllFundsRecordsFromServerCallback {
  (args: {
    funds: FundModel[];
    fundsById: { [fundId: string]: FundModel };
  }): void;
}
